import { useCallback, useEffect, useRef, useState } from 'react'

type FeedItem =
    | {
          type: 'video'
          videoIndex: number
      }
    | {
          type: 'ad'
      }

interface UseVerticalAdFlowParams {
    feedItems: FeedItem[]
    index: number
    scrollToIndex: (targetIndex: number) => void
    adSkippableMap?: Record<number, boolean>
}

export const useVerticalAdFlow = ({
    feedItems,
    index,
    scrollToIndex,
    adSkippableMap,
}: UseVerticalAdFlowParams) => {
    const [adCompletionMap, setAdCompletionMap] = useState<
        Record<number, boolean>
    >({})
    const [adHiddenMap, setAdHiddenMap] = useState<Record<number, boolean>>({})
    const [suppressAutoplayOnce, setSuppressAutoplayOnce] = useState(false)
    const [pendingHideIndex, setPendingHideIndex] = useState<number | null>(
        null,
    )
    const pendingHideTimeoutRef = useRef<number | null>(null)

    const activeItem = feedItems[index]
    const isAdScreenActive = activeItem?.type === 'ad' && !adHiddenMap[index]
    const shouldLockNavigation = isAdScreenActive && !adSkippableMap?.[index]

    const findNextVisibleIndex = (startIndex: number, direction: 1 | -1) => {
        let i = startIndex
        while (i >= 0 && i < feedItems.length) {
            if (!(feedItems[i].type === 'ad' && adHiddenMap[i])) {
                return i
            }
            i += direction
        }
        return null
    }

    const markAdHidden = useCallback((feedIndex: number) => {
        setAdHiddenMap((prev) => ({ ...prev, [feedIndex]: true }))
        setAdCompletionMap((prev) => ({ ...prev, [feedIndex]: true }))
    }, [])

    useEffect(() => {
        if (pendingHideIndex === null) return
        if (index === pendingHideIndex) return

        if (pendingHideTimeoutRef.current !== null) {
            window.clearTimeout(pendingHideTimeoutRef.current)
        }

        // Hide the ad after scroll/index settles to avoid abrupt card removal.
        pendingHideTimeoutRef.current = window.setTimeout(() => {
            markAdHidden(pendingHideIndex)
            setPendingHideIndex(null)
            pendingHideTimeoutRef.current = null
        }, 350)

        return () => {
            if (pendingHideTimeoutRef.current !== null) {
                window.clearTimeout(pendingHideTimeoutRef.current)
                pendingHideTimeoutRef.current = null
            }
        }
    }, [index, markAdHidden, pendingHideIndex])

    useEffect(() => {
        return () => {
            if (pendingHideTimeoutRef.current !== null) {
                window.clearTimeout(pendingHideTimeoutRef.current)
                pendingHideTimeoutRef.current = null
            }
        }
    }, [])

    const hideAdAndNavigate = (feedIndex: number) => {
        if (feedIndex !== index) {
            markAdHidden(feedIndex)
            return
        }

        const nextIndex = findNextVisibleIndex(feedIndex + 1, 1)
        if (nextIndex !== null) {
            scrollToIndex(nextIndex)
            setPendingHideIndex(feedIndex)
            return
        }

        const prevIndex = findNextVisibleIndex(feedIndex - 1, -1)
        if (prevIndex !== null) {
            setSuppressAutoplayOnce(true)
            scrollToIndex(prevIndex)
            setPendingHideIndex(feedIndex)
            return
        }

        // No destination screen, hide immediately.
        markAdHidden(feedIndex)
    }

    return {
        adCompletionMap,
        adHiddenMap,
        suppressAutoplayOnce,
        setSuppressAutoplayOnce,
        isAdScreenActive,
        shouldLockNavigation,
        findNextVisibleIndex,
        markAdHidden,
        hideAdAndNavigate,
    }
}
